import { Component, ElementRef, Inject, Input } from '@angular/core';
import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import { SlideCommonService } from '@app/application/common/slide-common.service';
import { LoggerService } from '@app/blocks/logger/services/logger.service';
import { DataService } from '@app/core/services/data.service';
import { WINDOW, WINDOW_PROVIDERS } from '@app/shared/services/window/window.service';
import { SignupDataService } from '../services/signup-data.service';
import { SignupService } from '../services/signup.service';
import { ConnectSlideComponent } from '@app/blocks/ui/slide/slide.component';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { FormlyModule } from '@ngx-formly/core';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { NextOfKinService } from './next-of-kin.service';
import { formatFormErrors } from '@app/shared/formly/form-config';

@Component({
    selector: 'app-next-of-kin',
    providers: [
        WINDOW_PROVIDERS
    ],
    imports: [
        CommonModule,
        FormlyModule,
        ConnectSlideComponent,
        SpinnerComponent,
        AsyncPipe,
        NgIf
    ],
    templateUrl: './next-of-kin.component.html',
    styleUrl: './next-of-kin.component.scss'
})
export class NextOfKinComponent {
    @Input() slideNumber: string | undefined;
    @Input() finalSlide: boolean = false;
    currentSlide = 'nextOfKin';
    errors: any = [];
    errorMessage: string = "";
    showFormErrors = false; //error summary
    applicationId: any;
    loading = true; // spinner
    showSubmit = false;
    submitting = false;
    isDisabled = false;
    active = this.signupService.getActive();

    form: FormGroup = new FormGroup({});
    fields: BehaviorSubject<any> = new BehaviorSubject([]);
    model: any = this.signupService.getNextOfKinModel();
    subscription = this.signupService.subscribeNextOfKinModel((data: any) => {
        this.model = data;

        this.loading = true;

        const observables: Observable<any>[] = [
            this.nextOfKinService.fetchSchema(this.signupService.getRefDataModel())
        ];

        this.dataService.ready(observables).pipe(takeUntil(this.destroy$)).subscribe({
            next: ([ formConfig ]) => {
                const model = data;

                if (model.applicationForm && model.applicationForm != this.formState.applicationForm) {
                    console.log("next of kin setting application form to " + data.applicationForm);
                    this.formState.applicationForm = data.applicationForm;
                    this.signupService.setFormState(this.formState);
                }
                //the above if condition is not triggered due to applicationForm key not existing on nextOfKin response, so the check below has been added
                else {
                    this.formState.applicationForm = this.signupService.getAdditionalDetailsModel().applicationForm;
                }

                this.logger.info('getNextOfKin result', data);

                this.fields.next([ formConfig ]);

                this.loading = false; // stop spinner
                this.showSubmit = true;
                this.showFormErrors = false;
            },
            error: () => {
                this.logger.error('next-of-kin.component.js dataService error');
                this.loading = false; // stop spinner
                this.showFormErrors = true;
                return null;
            }
        });
    });

    constructor(
        private logger: LoggerService,
        private signupService: SignupService,
        private signupDataService: SignupDataService,
        private dataService: DataService,
        private nextOfKinService: NextOfKinService,
        private slideCommon: SlideCommonService,
        private el: ElementRef,
        @Inject(WINDOW) private window: Window) {
    }

    private destroy$ = new Subject<void>();

    get formState() {
        return this.signupService.formState;
    }

    onSubmit() {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            console.log("VALID");

            this.signupService.setNextOfKinModel(this.model);

            this.errors = [];
            this.showFormErrors = false;

            this.isDisabled = true;
            this.submitting = true;

            this.model = this.signupService.addAddressIds(this.model);

            //firstValueFrom will throw an error if the post response is 200 but null
            this.signupDataService.postNextOfKin(this.model).pipe(takeUntil(this.destroy$)).subscribe({
                next: () => {
                    this.logger.success('next of kin post success');

                    this.formState.progress.nextOfKinComplete = true;
                    this.signupService.setFormState(this.formState);

                    this.signupService.goForward('nextOfKin');

                    this.submitting = false;
                    this.isDisabled = false;

                    this.errorMessage = '';

                    this.slideCommon.scroll(this.active.active);
                },
                error: (error: any) => {
                    this.isDisabled = false;
                    this.submitting = false;
                    this.logger.error('post nextOfKin error', error);

                    this.errorMessage = 'Error saving your next of kin details, please contact support.';
                }
            })
        }
        else {
            console.log("INVALID", this.form.errors)
            const renderTime = 500;
            const revealTime = renderTime + 250;

            setTimeout(() => {
                this.errors = formatFormErrors(this.el, this.form);
                this.showFormErrors = true;
            }, renderTime);

            setTimeout(() => {
                this.scrollToId('next-of-kin-error-summary');
            }, revealTime);
        }
    }

    onBack() {
        this.loading = false;
        this.isDisabled = false;
        this.signupService.goBack('nextOfKin');
        this.slideCommon.scroll(this.active.active);
    }

    scrollToId(id: string) {
        this.slideCommon.scrollToId(id, this.el, this.window);
    }
}
